import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQ_CATEGORIES = [
  {
    id: 'service',
    label: 'About the Service',
    items: [
      {
        q: 'How long does the review take?',
        a: 'Usually 3-5 minutes. Our AI analyses your document and provides a complete report with a rewritten compliant draft, all within minutes. No waiting days for a solicitor appointment.',
      },
      {
        q: 'What format do I need to upload my agreement in?',
        a: 'You can upload PDF, DOC, DOCX, or TXT files up to 10MB. You can also paste the text of your agreement directly into the editor.',
      },
      {
        q: 'Can I use the rewritten agreement immediately?',
        a: 'Yes. The rewritten agreement is ready to use immediately after review. We recommend having it reviewed by your solicitor as a final step, but many landlords and agents use the output directly.',
      },
    ],
  },
  {
    id: 'results',
    label: 'Understanding Results',
    items: [
      {
        q: 'What exactly does the report include?',
        a: 'The report includes a full clause-by-clause compliance analysis, a legal risk assessment for each clause, a fully rewritten compliant agreement, plain English explanations of all findings, and a downloadable PDF report with compliance score.',
      },
      {
        q: 'What does the compliance score mean?',
        a: 'The compliance score is a percentage reflecting how well your agreement meets current UK housing legislation requirements. A higher score indicates better compliance. Scores below 70% typically indicate significant risk that requires immediate attention.',
      },
    ],
  },
  {
    id: 'legal',
    label: 'Legal & Compliance',
    items: [
      {
        q: 'Is the AI-generated review legally valid?',
        a: 'Our AI review provides a thorough compliance analysis based on current UK legislation. While it is not a substitute for formal legal advice, it identifies the same issues a solicitor would at a fraction of the cost. Many property professionals use it as their primary compliance check.',
      },
      {
        q: 'Does the review cover the Renters Rights Bill 2025?',
        a: 'Yes. Our AI is fully updated to cover the Renters Rights Bill 2025, including the abolition of Section 21, new possession grounds, the Decent Homes Standard, Awaab\'s Law, the Property Portal, and all related provisions.',
      },
      {
        q: 'What if I disagree with a finding in the report?',
        a: 'Every finding includes a detailed explanation with references to the specific legislation. If you disagree, you can use the annotations to discuss the finding with your legal advisor. The rewritten clauses are suggestions and you retain full control over the final document.',
      },
    ],
  },
];

export function FaqSection() {
  const [activeCategory, setActiveCategory] = useState('service');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const currentFaqs = FAQ_CATEGORIES.find((c) => c.id === activeCategory)?.items || [];

  return (
    <section className="py-20 sm:py-28 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-500">
            Everything you need to know about our tenancy agreement review service
          </p>
        </div>

        <div className="animate-on-scroll flex items-center justify-center gap-2 mb-8 flex-wrap">
          {FAQ_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setOpenIndex(0); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeCategory === cat.id
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="animate-on-scroll bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-elevated divide-y divide-gray-100">
          {currentFaqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={faq.q}>
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-start justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3 pr-4">
                    <HelpCircle className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-800">{faq.q}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 pl-[3.25rem] animate-slide-down">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
